import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Cloud, Layers, RefreshCw, Download, Network, Cpu, Database, Globe, Lock, AlertTriangle } from "lucide-react";
import Logo from "../components/app/Logo";
import { api, API_BASE, formatApiErrorDetail } from "../lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const MG_COLORS = {
  // Azure CAF kinds
  tenant_root: "#5B5BD6", platform: "#0EA5E9", landing_zones: "#22C55E",
  decommissioned: "#666", sandbox: "#A1A1A1", identity: "#EF4444",
  management: "#F59E0B", connectivity: "#0D9488", corp: "#5B5BD6", online: "#0EA5E9",
  // AWS Control Tower / Organizations OU kinds
  root: "#FF9900", security: "#EF4444", log_archive: "#0EA5E9",
  audit: "#F59E0B", shared_services: "#0D9488", infrastructure: "#5B5BD6",
  workloads: "#22C55E", network: "#0D9488",
  // GCP Cloud Foundation Fabric folder kinds
  common: "#4285F4", folder: "#4285F4", prod: "#22C55E", nonprod: "#0EA5E9",
  logging: "#0EA5E9", monitoring: "#F59E0B",
};
const TIER_COLORS = { frontend: "#5B5BD6", compute: "#0EA5E9", data: "#0D9488", networking: "#F59E0B", security: "#EF4444" };

const KNOWN = new Set([
  "app_service","sql_db","storage","key_vault","log_analytics","firewall","bastion",
  "vpn_gateway","vnet","subnet","nsg","route_table","nic","public_ip","private_endpoint",
  "aks","function_app","cosmos_db","redis","service_bus","app_gateway","front_door",
  "ddos","sentinel","monitor","defender","entra_id",
]);
const NET_TYPES = new Set([
  // Azure
  "firewall","bastion","vpn_gateway","app_gateway","nsg","route_table","nic",
  "public_ip","private_endpoint","front_door","ddos",
  // AWS
  "transit_gateway","nat_gateway","alb","waf","cloudfront","route53",
  // GCP
  "cloud_nat","cloud_armor","load_balancer","cloud_dns","cloud_cdn",
]);

// Provider-aware vocabulary + accents. Renders the same JSON shape three
// different ways so users see the concepts they actually use on each cloud.
const PROVIDER_META = {
  azure: {
    label: "Azure Enterprise Scale",
    accent: "#0EA5E9",
    mgSingular: "Management group", mgShort: "MG",
    subSingular: "Subscription", subShort: "SUB",
    rgSingular: "Resource group", rgShort: "RG",
    vnetSingular: "VNet",
    generateCta: "Generate landing zone",
    emptyHeading: "Generate your Azure Landing Zone",
    emptyBody: "OpenAI will produce a full Microsoft CAF Enterprise-Scale hierarchy — management groups, subscriptions, resource groups, hub-and-spoke networking, Azure Policy assignments, and every resource your workload needs.",
  },
  aws: {
    label: "AWS Landing Zone",
    accent: "#FF9900",
    mgSingular: "Organizational unit", mgShort: "OU",
    subSingular: "Account", subShort: "ACCT",
    rgSingular: "Account scope", rgShort: "SCOPE",
    vnetSingular: "VPC",
    generateCta: "Generate landing zone",
    emptyHeading: "Generate your AWS Landing Zone",
    emptyBody: "OpenAI will produce an AWS Control Tower + Organizations layout — OUs, accounts (Audit / Log Archive / Shared Services / Network / Workloads), VPC hub-and-spoke via Transit Gateway, SCPs, and every service your workload needs.",
  },
  gcp: {
    label: "GCP Foundation",
    accent: "#4285F4",
    mgSingular: "Folder", mgShort: "FOLDER",
    subSingular: "Project", subShort: "PROJ",
    rgSingular: "Project scope", rgShort: "SCOPE",
    vnetSingular: "VPC network",
    generateCta: "Generate foundation",
    emptyHeading: "Generate your GCP Foundation",
    emptyBody: "OpenAI will produce a Cloud Foundation Fabric layout — Folders (Common / Workloads / Sandbox), Projects (Shared VPC host, Logging, Monitoring, Workloads), Shared VPC, Org Policies, and every service your workload needs.",
  },
};

// Generic tier icon for AWS/GCP resources where we don't ship provider SVGs.
function TierIcon({ tier, size = 22, color }) {
  const Icon = tier === "data" ? Database
    : tier === "networking" ? Network
    : tier === "security" ? Lock
    : tier === "frontend" ? Globe
    : Cpu;
  return <Icon size={size} strokeWidth={2.25} color={color} />;
}

const AzureIcon = ({ type, size = 22 }) => {
  const src = `/azure-icons/${KNOWN.has(type) ? type : "_fallback"}.svg`;
  return <img src={src} alt={type} width={size} height={size} draggable={false} style={{ display: "block" }} />;
};

// One resource icon component — dispatches to Azure SVGs or tier icons.
function ResourceIcon({ provider, azureType, tier, size = 26 }) {
  if (provider === "azure") return <AzureIcon type={azureType} size={size} />;
  const color = TIER_COLORS[tier] || "#0EA5E9";
  return <TierIcon tier={tier} size={size} color={color} />;
}

function autoLayout(ent) {
  const W_RES = 188, H_RES = 70, PAD = 14, GAP = 12;
  const mgs = ent.management_groups || [];
  const subs = ent.subscriptions || [];
  const rgs = ent.resource_groups || [];
  const resources = (ent.resources || []).filter((r) => !NET_TYPES.has(r.azure_type));

  const resByRg = {};
  resources.forEach((r) => { (resByRg[r.rg_id] = resByRg[r.rg_id] || []).push(r); });

  const rgBox = {};
  rgs.forEach((rg) => {
    const list = resByRg[rg.id] || [];
    const cols = Math.min(3, Math.max(1, Math.ceil(Math.sqrt(list.length || 1))));
    const rows = Math.max(1, Math.ceil(list.length / cols));
    rgBox[rg.id] = {
      w: cols * W_RES + (cols + 1) * GAP,
      h: 44 + rows * H_RES + (rows + 1) * GAP,
      cols, rows, list,
    };
  });

  const rgsBySub = {};
  rgs.forEach((rg) => { (rgsBySub[rg.subscription_id] = rgsBySub[rg.subscription_id] || []).push(rg); });

  const subBox = {};
  subs.forEach((s) => {
    const list = rgsBySub[s.id] || [];
    const w = Math.max(300, ...list.map((rg) => rgBox[rg.id]?.w || 200)) + PAD * 2;
    const h = 56 + list.reduce((a, rg) => a + (rgBox[rg.id]?.h || 80) + GAP, 0) + PAD;
    subBox[s.id] = { w, h, list };
  });

  const subsByMg = {};
  subs.forEach((s) => { (subsByMg[s.mg_id] = subsByMg[s.mg_id] || []).push(s); });

  const mgBox = {};
  mgs.forEach((m) => {
    const list = subsByMg[m.id] || [];
    const w = list.length ? list.reduce((a, s) => a + (subBox[s.id]?.w || 200) + GAP, GAP) + PAD : 260;
    const h = list.length ? 60 + Math.max(...list.map((s) => subBox[s.id]?.h || 120)) + PAD : 84;
    mgBox[m.id] = { w, h, list };
  });

  const positions = {};
  let cursorX = 24;
  const childrenOf = (id) => mgs.filter((m) => m.parent_id === id);
  const place = (mgId, depth = 0, yOffset = 0) => {
    const kids = childrenOf(mgId);
    if (!kids.length) {
      positions[mgId] = { x: cursorX, y: yOffset, depth };
      cursorX += mgBox[mgId].w + 28;
      return;
    }
    const start = cursorX;
    kids.forEach((k) => place(k.id, depth + 1, yOffset + mgBox[mgId].h + 28));
    const end = cursorX;
    const w = mgBox[mgId].w;
    positions[mgId] = { x: Math.max(start, (start + end - 28 - w) / 2), y: yOffset, depth };
  };
  const root = mgs.find((m) => !m.parent_id) || mgs[0];
  if (root) place(root.id, 0, 0);
  const totalH = Math.max(...Object.values(positions).map((p) => p.y + mgBox[Object.keys(positions).find((k) => positions[k] === p)]?.h || 0)) + 60;

  return { mgBox, subBox, rgBox, positions, W_RES, H_RES, PAD, GAP, totalWidth: cursorX + 60, totalHierarchyH: totalH };
}

function NetworkingDiagram({ ent, provider }) {
  const providerMeta = PROVIDER_META[provider] || PROVIDER_META.azure;
  const vnets = ent.networking?.vnets || [];
  const subnets = ent.networking?.subnets || [];
  const peerings = ent.networking?.peerings || [];
  const netResources = (ent.resources || []).filter((r) => NET_TYPES.has(r.azure_type));
  const subnetIdsByVnet = {};
  subnets.forEach((s) => { (subnetIdsByVnet[s.vnet_id] = subnetIdsByVnet[s.vnet_id] || []).push(s); });

  // VNet layout: hub centered top, spokes below in a row
  const VNET_W = 460, HUB_X = 80;
  const hubs = vnets.filter((v) => v.role === "hub");
  const spokes = vnets.filter((v) => v.role !== "hub");
  const hub = hubs[0] || vnets[0];
  const spokeStartY = 380;

  const positions = {};
  if (hub) positions[hub.id] = { x: 480, y: 40 };
  spokes.forEach((sp, i) => {
    positions[sp.id] = { x: 80 + i * (VNET_W + 40), y: spokeStartY };
  });

  const vnetCenter = (v) => {
    const p = positions[v.id];
    return { x: (p?.x || 0) + VNET_W / 2, y: (p?.y || 0) + 160 };
  };

  // Resources mapped to subnets by name match (the LLM often references subnet IDs in resource notes; fall back to first subnet of any vnet)
  const resBySubnet = {};
  netResources.forEach((r) => {
    // try to assign by simple heuristic: scan resource notes/name for subnet name match
    const sn =
      subnets.find((s) => (r.name || "").toLowerCase().includes(s.name.toLowerCase())) ||
      subnets.find((s) => (r.notes || "").toLowerCase().includes(s.name.toLowerCase())) ||
      // by tier hints
      (r.azure_type === "firewall" && subnets.find((s) => s.name.includes("Firewall"))) ||
      (r.azure_type === "bastion" && subnets.find((s) => s.name.includes("Bastion"))) ||
      (r.azure_type === "vpn_gateway" && subnets.find((s) => s.name.includes("Gateway"))) ||
      subnets[0];
    if (sn) (resBySubnet[sn.id] = resBySubnet[sn.id] || []).push(r);
  });

  const width = Math.max(1100, 80 + spokes.length * (VNET_W + 40));
  const height = spokeStartY + 360;

  const renderVnetBox = (v) => {
    const p = positions[v.id];
    if (!p) return null;
    const sns = subnetIdsByVnet[v.id] || [];
    const isHub = v.role === "hub";
    const color = isHub ? "#F59E0B" : "#0EA5E9";
    return (
      <div
        key={v.id}
        data-testid={`vnet-${v.id}`}
        style={{
          position: "absolute", left: p.x, top: p.y, width: VNET_W,
          minHeight: 300, borderRadius: 16, border: `1.5px dashed ${color}`,
          background: `${color}0D`, padding: 14,
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          {provider === "azure" ? <AzureIcon type="vnet" size={18} /> : <Network size={18} color={color} />}
          <span className="font-mono" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color }}>{isHub ? `Hub ${providerMeta.vnetSingular}` : `Spoke ${providerMeta.vnetSingular}`}</span>
          <span className="text-text-primary" style={{ fontSize: 13, fontWeight: 600 }}>{v.name}</span>
          <span className="font-mono text-text-tertiary ml-auto" style={{ fontSize: 11 }}>{v.address_space}</span>
        </div>
        <div className="flex flex-col gap-2">
          {sns.map((sn) => {
            const list = resBySubnet[sn.id] || [];
            return (
              <div key={sn.id} data-testid={`subnet-${sn.id}`} style={{
                borderRadius: 10, border: "1px dashed var(--color-border-primary)",
                background: "var(--color-background-secondary)", padding: 10,
              }}>
                <div className="flex items-center gap-2 mb-2">
                  {provider === "azure"
                    ? <AzureIcon type="subnet" size={14} />
                    : <Network size={12} color="var(--color-text-tertiary)" />}
                  <span className="text-text-primary truncate" style={{ fontSize: 12, fontWeight: 500 }}>{sn.name}</span>
                  <span className="font-mono text-text-tertiary ml-auto" style={{ fontSize: 10 }}>{sn.address_prefix}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {list.length === 0 && (
                    <span className="font-mono text-text-tertiary" style={{ fontSize: 10 }}>{sn.purpose || "—"}</span>
                  )}
                  {list.map((r) => (
                    <div
                      key={r.id}
                      data-testid={`netres-${r.id}`}
                      title={`${r.name} · $${r.monthly_cost_usd}/mo`}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "4px 8px", borderRadius: 8,
                        border: `1px solid var(--color-border-primary)`,
                        background: "var(--color-background-tertiary)",
                      }}
                    >
                      <ResourceIcon provider={provider} azureType={r.azure_type} tier={r.tier} size={16} />
                      <span className="text-text-primary truncate" style={{ fontSize: 11, fontWeight: 500, maxWidth: 130 }}>{r.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-10 border-t border-border-primary pt-8" data-testid="network-diagram">
      <div className="flex items-center gap-2 mb-6">
        <Network size={16} color="#F59E0B" />
        <h3 className="text-text-primary" style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em" }}>Hub-and-spoke networking</h3>
        <span className="font-mono text-text-tertiary" style={{ fontSize: 11 }}>· {vnets.length} {providerMeta.vnetSingular}s · {subnets.length} subnets · {peerings.length} peerings</span>
      </div>
      <div className="relative" style={{ width, height }}>
        <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={width} height={height}>
          {peerings.map((pe) => {
            const a = vnets.find((v) => v.id === pe.source_vnet_id);
            const b = vnets.find((v) => v.id === pe.target_vnet_id);
            if (!a || !b) return null;
            const c1 = vnetCenter(a), c2 = vnetCenter(b);
            const midY = (c1.y + c2.y) / 2 + 60;
            return (
              <g key={pe.id} data-testid={`peering-${pe.id}`}>
                <path d={`M ${c1.x} ${c1.y} Q ${(c1.x + c2.x) / 2} ${midY} ${c2.x} ${c2.y}`} stroke="#F59E0B" strokeWidth="2" strokeDasharray="6 4" fill="none" opacity="0.8" />
                <circle cx={(c1.x + c2.x) / 2} cy={midY} r="14" fill="#1A1A1A" stroke="#F59E0B" />
                <text x={(c1.x + c2.x) / 2} y={midY + 4} fill="#F59E0B" fontSize="10" textAnchor="middle" fontFamily="JetBrains Mono">peer</text>
              </g>
            );
          })}
        </svg>
        {vnets.map((v) => renderVnetBox(v))}
      </div>
    </div>
  );
}

export default function ProjectEnterprise() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [resOverride, setResOverride] = useState({});
  const saveTimer = useRef(null);

  useEffect(() => {
    api.get(`/projects/${id}`).then(({ data }) => {
      setProject(data);
      if (data.enterprise?._overrides) setResOverride(data.enterprise._overrides);
    }).catch(() => navigate("/dashboard"));
  }, [id, navigate]);

  const ent = project?.enterprise;
  const provider = (ent?.provider || project?.selected_provider || "azure").toLowerCase();
  const exportVersion = Math.max(1, ...(project?.hierarchy_versions || [])
    .map((item) => item?.version).filter((version) => Number.isInteger(version) && version > 0));
  const exportSlug = (project?.name || "architecture").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 64).replace(/-$/g, "") || "architecture";
  const enterpriseFilename = `${exportSlug}-${provider}-v${exportVersion}-enterprise.tf`;
  const meta = PROVIDER_META[provider] || PROVIDER_META.azure;
  const layout = useMemo(() => (ent ? autoLayout(ent) : null), [ent]);

  const totalCost = useMemo(
    () => (ent?.resources || []).reduce((s, r) => s + Number(r.monthly_cost_usd || 0), 0),
    [ent]
  );

  // Terraform-download confirm dialog (AWS/GCP only — Azure is idempotent
  // enough to skip the friction).
  const [confirmTfOpen, setConfirmTfOpen] = useState(false);
  const requiresTfConfirm = provider !== "azure";
  const startTfDownload = () => {
    // Trigger the browser to hit the export endpoint (session cookie carries auth).
    window.location.href = `${API_BASE}/projects/${id}/export/enterprise-terraform`;
  };
  const onTfClick = (e) => {
    if (!requiresTfConfirm) return; // Azure: let the <a href> fire.
    e.preventDefault();
    setConfirmTfOpen(true);
  };
  const tfCounts = useMemo(() => ({
    ous: (ent?.management_groups || []).length,
    accounts: (ent?.subscriptions || []).length,
    resources: (ent?.resources || []).length,
    policies: (ent?.policies || []).length,
  }), [ent]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/projects/${id}/generate-enterprise`);
      setProject((p) => ({ ...p, enterprise: data.enterprise }));
      setResOverride({});
      toast.success("Landing zone generated", {
        description: `$${data.monthly_cost_total_usd}/mo across ${data.enterprise.resources?.length || 0} resources`,
      });
    } catch (e) {
      toast.error("Generation failed", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setGenerating(false);
    }
  };

  const persistOverride = (next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.patch(`/projects/${id}`, { enterprise: { ...ent, _overrides: next } }).catch(() => {});
    }, 500);
  };

  const onResPointerDown = (resId, e, baseX, baseY) => {
    const sx = e.clientX, sy = e.clientY;
    const orig = resOverride[resId] || { x: baseX, y: baseY };
    const mv = (ev) => {
      const x = orig.x + (ev.clientX - sx), y = orig.y + (ev.clientY - sy);
      setResOverride((p) => { const n = { ...p, [resId]: { x, y } }; persistOverride(n); return n; });
    };
    const up = () => { window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
  };

  if (!project) return <div className="min-h-screen grid place-items-center bg-bg-primary"><div className="skeleton h-6 w-40" /></div>;

  return (
    <div className="min-h-screen bg-bg-primary" data-testid="enterprise-page">
      <header className="border-b border-border-primary sticky top-0 z-40" style={{ background: "rgba(10,10,10,0.92)", backdropFilter: "blur(12px)" }}>
        <div className="px-6 h-[64px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span style={{ color: "var(--color-border-primary)" }}>·</span>
            <h1 className="text-text-primary" style={{ fontSize: 14, fontWeight: 500 }}>{project.name}</h1>
            <span className="font-mono rounded-full" style={{ padding: "2px 8px", fontSize: 10, letterSpacing: "0.08em", color: meta.accent, background: `${meta.accent}1A`, border: `1px solid ${meta.accent}66`, textTransform: "uppercase" }} data-testid="enterprise-provider-pill">{meta.label}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Labelled "Conceptual" and pointed at /projects/:id until 2026-08-06,
                which was a link to a redirect shim that lands back on /output — the
                Conceptual Canvas UI it referred to no longer exists. Now says where
                it actually goes. */}
            <Link to={`/projects/${id}/output`} data-testid="back-to-output" className="motion-micro inline-flex items-center gap-1.5" style={{ fontSize: 13, color: "var(--color-text-secondary)", height: 36, padding: "0 12px", borderRadius: 8, border: "1px solid var(--color-border-primary)" }}><Layers size={14} /> Architecture</Link>
            {ent && (
              <a
                href={`${API_BASE}/projects/${id}/export/enterprise-terraform`}
                onClick={onTfClick}
                data-testid="export-enterprise-tf"
                className="motion-micro inline-flex items-center gap-1.5"
                style={{ fontSize: 13, color: "var(--color-text-secondary)", height: 36, padding: "0 12px", borderRadius: 8, border: "1px solid var(--color-border-primary)", textDecoration: "none" }}
              >
                <Download size={14} /> Terraform
              </a>
            )}
            <button onClick={generate} disabled={generating} data-testid="generate-enterprise-btn" className="motion-micro inline-flex items-center gap-1.5" style={{ fontSize: 13, color: "#0a0a0a", background: "var(--color-accent)", height: 36, padding: "0 14px", borderRadius: 8, border: "none", cursor: generating ? "not-allowed" : "pointer", opacity: generating ? 0.7 : 1 }}>
              {ent ? <RefreshCw size={14} /> : <Sparkles size={14} />} {generating ? "Generating…" : ent ? "Regenerate" : meta.generateCta}
            </button>
            <Link to="/dashboard" data-testid="back-to-dashboard" className="motion-micro inline-flex items-center gap-1.5" style={{ fontSize: 13, color: "var(--color-text-secondary)", height: 36, padding: "0 12px", borderRadius: 8, border: "1px solid var(--color-border-primary)" }}><ArrowLeft size={14} /> Back</Link>
          </div>
        </div>
      </header>

      {!ent && (
        <div className="grid place-items-center py-24" data-testid="enterprise-empty">
          <div className="rounded-[16px] border border-dashed p-10 text-center" style={{ borderColor: "var(--color-border-primary)", background: "var(--color-background-secondary)", maxWidth: 520 }}>
            <Cloud size={32} color="var(--color-accent)" className="mx-auto mb-4" />
            <h2 className="text-text-primary mb-2" style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" }}>{meta.emptyHeading}</h2>
            <p className="text-text-secondary mb-6" style={{ fontSize: 13, lineHeight: 1.6 }}>
              {meta.emptyBody}
            </p>
            <button onClick={generate} disabled={generating} data-testid="enterprise-empty-cta" className="motion-micro inline-flex items-center gap-2 rounded-[8px]" style={{ background: "var(--color-accent)", color: "#0a0a0a", height: 44, padding: "0 18px", fontSize: 14, fontWeight: 500, border: "none", cursor: "pointer" }}>
              <Sparkles size={14} /> {generating ? "Generating…" : meta.generateCta}
            </button>
          </div>
        </div>
      )}

      {ent && layout && (
        <div className="overflow-auto" style={{ height: "calc(100vh - 64px)" }}>
          <div className="relative" style={{ width: layout.totalWidth, minHeight: 1200, padding: 24, backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)", backgroundSize: "24px 24px" }} data-testid="enterprise-canvas">
            <div className="mb-6 flex items-center gap-4">
              <span className="font-mono text-text-tertiary" style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>Total estimated</span>
              <span className="text-text-primary" style={{ fontSize: 22, fontWeight: 600 }}>${totalCost.toFixed(2)}<span className="text-text-tertiary" style={{ fontSize: 13, fontWeight: 400 }}>/mo</span></span>
              <span className="font-mono text-text-tertiary" style={{ fontSize: 11 }}>· {ent.resources?.length || 0} resources · {ent.management_groups?.length || 0} {meta.mgShort}s · {ent.subscriptions?.length || 0} {meta.subShort.toLowerCase()}s · {(ent.policies || []).length} policies</span>
            </div>

            {/* Management group hierarchy */}
            {(ent.management_groups || []).map((mg) => {
              const pos = layout.positions[mg.id];
              const box = layout.mgBox[mg.id];
              if (!pos || !box) return null;
              const color = MG_COLORS[mg.kind] || "#5B5BD6";
              return (
                <div key={mg.id} data-testid={`mg-${mg.id}`} style={{ position: "absolute", left: pos.x, top: 60 + pos.y, width: box.w, minHeight: box.h, borderRadius: 14, border: `1.5px dashed ${color}`, background: `${color}0A`, padding: 14 }}>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Layers size={13} color={color} />
                    <span className="font-mono" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color }}>{mg.kind || meta.mgShort}</span>
                    <span className="text-text-primary" style={{ fontSize: 13, fontWeight: 600 }}>{mg.name}</span>
                    {(ent.policies || []).filter((p) => p.mg_id === mg.id).slice(0, 3).map((p) => {
                      const c = p.severity === "deny" ? "#EF4444" : p.severity === "append" ? "#F59E0B" : "#22C55E";
                      return (
                        <span key={p.id} data-testid={`policy-${p.id}`} title={p.description} className="font-mono rounded-full" style={{ padding: "1px 7px", fontSize: 9, color: c, background: `${c}1A`, border: `1px solid ${c}66` }}>
                          {p.severity?.toUpperCase()} · {p.name}
                        </span>
                      );
                    })}
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    {(box.list || []).map((sub) => {
                      const sb = layout.subBox[sub.id];
                      if (!sb) return null;
                      return (
                        <div key={sub.id} data-testid={`sub-${sub.id}`} style={{ width: sb.w, minHeight: sb.h, borderRadius: 12, border: "1px solid var(--color-border-primary)", background: "var(--color-background-secondary)", padding: 12 }}>
                          <div className="flex items-center gap-2 mb-3">
                            <Cloud size={12} color={meta.accent} />
                            <span className="font-mono" style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: meta.accent }}>{sub.purpose || meta.subShort}</span>
                            <span className="text-text-primary truncate" style={{ fontSize: 12, fontWeight: 500 }} title={sub.name}>{sub.name}</span>
                          </div>
                          <div className="flex flex-col gap-2">
                            {(sb.list || []).map((rg) => {
                              const rb = layout.rgBox[rg.id];
                              if (!rb) return null;
                              return (
                                <div key={rg.id} data-testid={`rg-${rg.id}`} style={{ width: rb.w, minHeight: rb.h, borderRadius: 10, border: "1px dashed var(--color-border-primary)", background: "var(--color-background-tertiary)", padding: 10 }}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-mono text-text-tertiary" style={{ fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase" }}>{meta.rgShort}</span>
                                    <span className="text-text-primary truncate" style={{ fontSize: 11, fontWeight: 500 }}>{rg.name}</span>
                                    <span className="font-mono text-text-tertiary ml-auto" style={{ fontSize: 9 }}>{rg.region}</span>
                                  </div>
                                  <div style={{ position: "relative", height: rb.rows * 70 + (rb.rows + 1) * 12 }}>
                                    {(rb.list || []).map((res, idx) => {
                                      const col = idx % rb.cols, row = Math.floor(idx / rb.cols);
                                      const baseX = 12 + col * (188 + 12);
                                      const baseY = 12 + row * (70 + 12);
                                      const ov = resOverride[res.id];
                                      const x = ov ? ov.x : baseX, y = ov ? ov.y : baseY;
                                      const tierColor = TIER_COLORS[res.tier] || "#0EA5E9";
                                      return (
                                        <div
                                          key={res.id}
                                          data-testid={`res-${res.id}`}
                                          onPointerDown={(e) => onResPointerDown(res.id, e, baseX, baseY)}
                                          style={{
                                            position: "absolute", left: x, top: y, width: 188, height: 70,
                                            borderRadius: 8,
                                            border: "1px solid var(--color-border-primary)",
                                            borderLeft: `3px solid ${tierColor}`,
                                            background: "var(--color-background-secondary)",
                                            padding: "8px 10px",
                                            cursor: "grab", touchAction: "none",
                                            display: "flex", alignItems: "center", gap: 10,
                                            boxShadow: "0 6px 14px -8px rgba(0,0,0,0.55)",
                                          }}
                                        >
                                          <ResourceIcon provider={provider} azureType={res.azure_type} tier={res.tier} size={26} />
                                          <div className="min-w-0">
                                            <p className="font-mono" style={{ fontSize: 9, color: tierColor, letterSpacing: "0.06em", textTransform: "uppercase" }}>{(res.azure_type || "").replace(/_/g, " ")}</p>
                                            <p className="text-text-primary truncate" style={{ fontSize: 11, fontWeight: 500 }} title={res.name}>{res.name}</p>
                                            <p className="font-mono text-text-tertiary" style={{ fontSize: 10 }}>${Number(res.monthly_cost_usd || 0).toFixed(0)}/mo</p>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Networking layer below the hierarchy */}
            <div style={{ position: "absolute", left: 24, right: 24, top: 60 + (layout.totalHierarchyH || 800) + 40 }}>
              <NetworkingDiagram ent={ent} provider={provider} />
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={confirmTfOpen} onOpenChange={setConfirmTfOpen}>
        <AlertDialogContent data-testid="tf-confirm-dialog" className="border border-[var(--color-border-primary)]" style={{ background: "var(--color-background-secondary)" }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-text-primary">
              <AlertTriangle size={16} color="#F59E0B" />
              Review before you run `terraform apply`
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-text-secondary" style={{ fontSize: 13, lineHeight: 1.6 }}>
                <p className="mb-3">
                  You&apos;re about to download Terraform that will <span className="text-text-primary" style={{ fontWeight: 500 }}>CREATE</span> real infrastructure in {meta.label}:
                </p>
                <ul className="mb-3 space-y-1" style={{ fontSize: 12 }}>
                  <li className="font-mono text-text-primary">· {tfCounts.ous} {meta.mgShort.toLowerCase()}{tfCounts.ous === 1 ? "" : "s"}</li>
                  <li className="font-mono text-text-primary">· {tfCounts.accounts} {meta.subShort.toLowerCase()}{tfCounts.accounts === 1 ? "" : "s"} {provider === "aws" ? " — account creation is IRREVERSIBLE" : provider === "gcp" ? " — projects consume billing account quota" : ""}</li>
                  <li className="font-mono text-text-primary">· {tfCounts.resources} resources</li>
                  {tfCounts.policies > 0 && (
                    <li className="font-mono text-text-primary">· {tfCounts.policies} {provider === "aws" ? "SCP" : "org"} polic{tfCounts.policies === 1 ? "y" : "ies"}</li>
                  )}
                </ul>
                <p className="mb-1" style={{ fontWeight: 500, color: "var(--color-text-primary)" }}>
                  Head the file before applying:
                </p>
                <p className="font-mono" style={{ fontSize: 11, background: "var(--color-background-tertiary)", padding: "6px 10px", borderRadius: 6, border: "1px solid var(--color-border-primary)" }}>
                  head -30 {enterpriseFilename}
                </p>
                <p className="mt-3" style={{ fontSize: 12 }}>
                  The file starts with a <span className="text-text-primary">PRE-APPLY CHECKLIST</span> listing every `REPLACE_WITH_*` value plus provider-specific gotchas. <span style={{ fontWeight: 500 }}>Swap those placeholders and run `terraform plan` first.</span>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tf-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="tf-confirm-download"
              onClick={() => { setConfirmTfOpen(false); startTfDownload(); }}
              style={{ background: "var(--color-accent)", color: "#0a0a0a" }}
            >
              I understand — download
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
